import { FormFieldOverride } from 'components/form/formTypes'
import { formatRaw } from 'features/libra/llmDiff/formatFieldValue'
import { FFS_FIELD_OVERRIDES } from 'features/libra/stepComponents/ffs/ffsAssetModelFieldOverrides'
import { isFieldHiddenInFfs } from 'features/libra/stepComponents/ffs/ffsHiddenAssetModelFields'
import isEqual from 'lodash/isEqual'

export interface AssetModelDiffRow {
  label: string
  /** e.g. `['wall_thickness']` or `['features', '<featureId>', '<field>']`. */
  path: string[]
  status: 'changed' | 'added' | 'removed'
  /** Already display-formatted via `formatRaw`. */
  before: string
  /** Already display-formatted via `formatRaw`. */
  after: string
}

export interface AssetModelDiffGroup {
  componentId: string
  componentName: string
  rows: AssetModelDiffRow[]
}

/**
 * Diffs the `raw.data.components` record of two `GeckoAssetModel`s (a
 * ticket's snapshot vs the unit's live master) into per-component groups of
 * changed/added/removed leaf fields.
 *
 * Walks each component recursively: primitives, `null`/`undefined`, and
 * arrays are leaves compared with `isEqual`; plain objects recurse (this is
 * what surfaces nested `features.<id>.<field>` diffs). Top-level component
 * scalars hidden from the FFS asset-model editor (`isFieldHiddenInFfs`) are
 * skipped so this drawer never surfaces noise the editor itself hides —
 * nested feature fields are never filtered this way.
 */
export function diffAssetModelComponents(
  before: Record<string, unknown>,
  after: Record<string, unknown>
): AssetModelDiffGroup[] {
  const componentIds = new Set([...Object.keys(before), ...Object.keys(after)])
  const groups: AssetModelDiffGroup[] = []

  for (const componentId of componentIds) {
    const beforeComponent = asPlainObject(before[componentId])
    const afterComponent = asPlainObject(after[componentId])
    // Defensive: a component value that isn't an object (malformed data) on
    // both sides contributes nothing diffable.
    if (!beforeComponent && !afterComponent) continue

    const rows = diffObject(beforeComponent ?? {}, afterComponent ?? {}, [])
    if (rows.length === 0) continue

    groups.push({
      componentId,
      componentName: resolveComponentName(
        afterComponent,
        beforeComponent,
        componentId
      ),
      rows,
    })
  }

  return groups.sort((a, b) => a.componentName.localeCompare(b.componentName))
}

function resolveComponentName(
  after: Record<string, unknown> | undefined,
  before: Record<string, unknown> | undefined,
  componentId: string
): string {
  const afterName = after?.name
  if (typeof afterName === 'string' && afterName.trim() !== '') {
    return afterName
  }
  const beforeName = before?.name
  if (typeof beforeName === 'string' && beforeName.trim() !== '') {
    return beforeName
  }
  return componentId
}

function asPlainObject(value: unknown): Record<string, unknown> | undefined {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return undefined
  }
  return value as Record<string, unknown>
}

function diffObject(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  path: string[]
): AssetModelDiffRow[] {
  const rows: AssetModelDiffRow[] = []
  const keys = new Set([...Object.keys(before), ...Object.keys(after)])

  for (const key of keys) {
    const childPath = [...path, key]

    // Only the FFS editor's hidden set of top-level component scalars is
    // filtered -- nested (e.g. `features.<id>.<field>`) rows are never hidden.
    if (childPath.length === 1 && isFieldHiddenInFfs(key)) continue

    const beforeValue = before[key]
    const afterValue = after[key]
    const beforeIsObject = asPlainObject(beforeValue)
    const afterIsObject = asPlainObject(afterValue)

    if (beforeIsObject || afterIsObject) {
      rows.push(
        ...diffObject(beforeIsObject ?? {}, afterIsObject ?? {}, childPath)
      )
      continue
    }

    const hasBefore = key in before
    const hasAfter = key in after
    if (hasBefore && hasAfter && isEqual(beforeValue, afterValue)) continue

    const status: AssetModelDiffRow['status'] = !hasBefore
      ? 'added'
      : !hasAfter
        ? 'removed'
        : 'changed'

    rows.push(
      buildRow(
        childPath,
        hasBefore ? beforeValue : undefined,
        hasAfter ? afterValue : undefined,
        status
      )
    )
  }

  return rows
}

function buildRow(
  path: string[],
  beforeValue: unknown,
  afterValue: unknown,
  status: AssetModelDiffRow['status']
): AssetModelDiffRow {
  const override: FormFieldOverride | undefined =
    path.length === 1 ? FFS_FIELD_OVERRIDES[path[0]] : undefined

  return {
    label: override?.label ?? humanizeLabel(path[path.length - 1]),
    path,
    status,
    before: formatRaw(applyToDisplay(beforeValue, override)),
    after: formatRaw(applyToDisplay(afterValue, override)),
  }
}

function applyToDisplay(
  value: unknown,
  override: FormFieldOverride | undefined
): unknown {
  if (override?.toDisplay && typeof value === 'number') {
    return override.toDisplay(value)
  }
  return value
}

/** `wall_thickness` -> `Wall thickness`. */
function humanizeLabel(segment: string): string {
  const spaced = segment.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}
